import type { MessageKey, TranslationParams } from '../i18n';

export interface DropZoneOptions {
  translate: (key: MessageKey, params?: TranslationParams) => string;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}

export function renderDropZone(options: DropZoneOptions): string {
  const t = options.translate;
  return `
    <section class="import-panel" aria-labelledby="import-title">
      <div class="drop-zone" data-testid="drop-zone" data-drop-zone aria-describedby="drop-hint" aria-disabled="${String(Boolean(options.disabled))}">
        <input
          hidden
          id="png-file-input"
          type="file"
          accept="image/png,.png"
          multiple
          ${options.disabled ? 'disabled' : ''}
          aria-label="${t('fileInputLabel')}"
          data-testid="file-input"
        />
        <span class="drop-zone__icon" aria-hidden="true">＋</span>
        <div class="drop-zone__copy">
          <h2 id="import-title">${t('dropTitle')}</h2>
          <p>${t('dropBody')}</p>
        </div>
        <button class="button button--primary drop-zone__button" type="button" data-testid="choose-files" aria-describedby="drop-hint" ${options.disabled ? 'disabled' : ''}>${t('chooseFiles')}</button>
        <p class="drop-zone__hint" id="drop-hint">${t('dropHint')}</p>
      </div>
      <div class="privacy-note">
        <span class="status-icon" aria-hidden="true">✓</span>
        <div>
          <strong>${t('privacyTitle')}</strong>
          <span>${t('privacyBody')}</span>
        </div>
      </div>
    </section>
  `;
}

export function bindDropZone(container: HTMLElement, options: DropZoneOptions): void {
  const zone = container.querySelector<HTMLElement>('[data-drop-zone]');
  const input = container.querySelector<HTMLInputElement>('#png-file-input');
  const chooseButton = container.querySelector<HTMLButtonElement>('[data-testid="choose-files"]');
  if (!zone || !input || !chooseButton) return;

  chooseButton.addEventListener('click', () => input.click());

  input.addEventListener('change', () => {
    options.onFiles(Array.from(input.files ?? []));
    input.value = '';
  });

  const setDragging = (dragging: boolean): void => {
    zone.classList.toggle('is-dragging', dragging);
  };
  zone.addEventListener('dragenter', (event) => {
    event.preventDefault();
    if (!options.disabled) setDragging(true);
  });
  zone.addEventListener('dragover', (event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = options.disabled ? 'none' : 'copy';
  });
  zone.addEventListener('dragleave', (event) => {
    if (!zone.contains(event.relatedTarget as Node | null)) setDragging(false);
  });
  zone.addEventListener('drop', (event) => {
    event.preventDefault();
    setDragging(false);
    if (!options.disabled) options.onFiles(Array.from(event.dataTransfer?.files ?? []));
  });
}
